import React from 'react';
import { Container } from 'components/shared';
import styled, { css } from 'styled-components';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import cx from 'classnames';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { sitecoreApiHost } from 'temp/config';
import OpenNewTab from 'components/icons/OpenNewTab';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';

const boxShadow = css`
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.2);
`;
const CustomButton = styled.button`
  min-height: 3rem;
`;

const StyledDiv = styled.div`
  transition: box-shadow 0.2s ease-in-out;
  ${({ shadowed }) => shadowed && boxShadow}
  ${({ shadowedOnHover }) =>
    shadowedOnHover &&
    `
    border-radius: 0.625rem;
    &:hover {
      border-color: white;
      ${boxShadow}
    }
  `}
`;

const isSameDomain = (src = '') => {
  let texturl = '';
  let domain = '';
  let hasError = false;
  try {
    texturl = new URL(src);
    domain = texturl.hostname;
  } catch (e) {
    texturl = src;
    hasError = true;
  }
  return hasError ? false : sitecoreApiHost === domain;
};

const getFileName = (url) => {
  const [_name, _, extension] = url
    .substring(url.lastIndexOf('/') + 1, url.length)
    .split('.');
  return extension ? [_name + _, extension] : [_name, _];
};
const downloadFile = async (url) => {
  const proxy = ''; // "https://cors-anywhere.herokuapp.com/";
  const [name, extension] = getFileName(url);
  fetch(proxy + url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/' + extension,
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': '*',
      'Access-Control-Allow-Headers': '*',
      'Access-Control-Allow-Credentials': 'true',
      'Access-Control-Expose-Headers': 'Authorization',
    },
  })
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name + '.' + extension);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    })
    .catch((e) => console.log(e));
};

const Wrapper = ({ favoritesBanner, children }) =>
  !favoritesBanner ? (
    <Container>{children}</Container>
  ) : (
    <div className='w-full'>{children}</div>
  );

const DescriptionTitle = ({ title }) => {
  return (
    <div className='w-full flex flex-row justify-between'>
      <p
        className={cx(
          'h-8 font-bold font-trade-gothic-20 text-center md:text-left',
          'md:mt-4 mb-5 md:mb-2 md:mr-8 leading-tight',
          'text-3xl mb-1 md:mb-2',
        )}
      >
        {title}
      </p>
    </div>
  );
};
// File
const DescriptionLink = ({
  description,
  cta,
  textStyle = 'blue-white',
  type = '',
}) => {
  const [extension] = getFileName(cta.href);
  let ariaLabelType = type;
  let ariaLabelText = cta.label || description;
  let opensNewTabFlag = true;
  if (ariaLabelType === '') {
    if (extension) {
      ariaLabelType = 'File';
      if (isSameDomain(cta.href)) opensNewTabFlag = false;
    } else {
      ariaLabelType = 'Link';
    }
  }
  return (
    <CustomButton
      className={cx(
        'w-full flex flex-row justify-between items-center rounded my-2 py-1',
        {
          'bg-white': textStyle === 'blue-white',
          'bg-mattamy-blue': textStyle === 'white-blue',
          'bg-light-blue': textStyle === 'blue-blue',
        },
      )}
      label={'open-content'}
      aria-label={
        ariaLabelType === 'Link'
          ? 'Open Link ' + ariaLabelText + ' in a new tab.'
          : ariaLabelText +
            ' PDF' +
            (opensNewTabFlag ? ' (opens in a new tab).' : '')
      }
      onClick={() => {
        trackAnalyticsEvent({
          gtagEvent: 'DownloadClick',
          mouseflowTag: 'Download',
        });
        if (extension && isSameDomain(cta.href)) {
          downloadFile(cta.href);
        } else {
          window.open(cta.href, '_blank');
        }
      }}
    >
      <p
        className={cx(
          'flex font-bold font-trade-gothic-20 leading-tight text-left text-xl mt-1 w-10/12',
          {
            'text-mattamy-blue': textStyle === 'blue-white',
            'ml-4 text-white': textStyle === 'white-blue',
            'ml-4 text-mattamy-blue': textStyle === 'blue-blue',
          },
        )}
      >
        {description}
      </p>
      <div className='flex flex-row items-center justify-between'>
        {opensNewTabFlag && (
          <OpenNewTab className='w-4 ml-1 text-white mr-2  ' />
        )}
      </div>
    </CustomButton>
  );
};

const SplitItem = ({ data }) => {
  if (!data) return null;
  if (
    (data.firstCTALink && data.firstTitle && data.firstCTALink.href) ||
    (data.secondCTALink && data.secondTitle && data.secondCTALink.href)
  ) {
    return (
      <div className='flex flex-col w-full mb-4 md:mb-0'>
        {data.header && <DescriptionTitle title={data.header} />}
        {data.firstCTALink && data.firstTitle && data.firstCTALink.href && (
          <DescriptionLink
            description={data.firstTitle}
            cta={data.firstCTALink}
            textStyle={data.textStyle || 'blue-white'}
            type={data.firstCTAType}
          />
        )}
        {data.secondCTALink && data.secondTitle && data.secondCTALink.href && (
          <DescriptionLink
            description={data.secondTitle}
            cta={data.secondCTALink}
            textStyle={data.textStyle || 'blue-white'}
            type={data.secondCTAType}
          />
        )}
      </div>
    );
  }
  return null;
};

const hasTitle = (e) => (e.header ? 1 : 0);
const hasCTAs = (e) =>
  e.firstCTALink &&
  e.firstTitle &&
  e.firstCTALink.href &&
  e.secondCTALink &&
  e.secondTitle &&
  e.secondCTALink.href
    ? 2
    : 1;
const getFirstColumnElements = ({
  splitComponentItems,
  totalTitles,
  totalCTAs,
}) => {
  let ctasList = [];
  let total = totalTitles + totalCTAs;
  let counter = 0;
  for (let i = 0; i < splitComponentItems.length; i++) {
    if (counter < total / 2 + 1) {
      const eTitle = hasTitle(splitComponentItems[i]);
      const eCtas = hasCTAs(splitComponentItems[i]);
      counter = counter + eTitle + eCtas;
      ctasList.push(splitComponentItems[i]);
    }
  }
  return ctasList;
};

const SplitComponent = ({ fields, rendering }) => {
  const splitComponent = getFieldValue(fields, 'splitComponent');
  const splitComponentList = splitComponent && splitComponent.splitTitleCtaVMs;
  const splitComponentItems = splitComponentList.filter(
    (e) =>
      (e.firstCTALink && e.firstTitle && e.firstCTALink.href) ||
      (e.secondCTALink && e.secondTitle && e.secondCTALink.href),
  );
  const totalTitles = splitComponentItems.reduce(
    (acc, e) => acc + hasTitle(e),
    0,
  );
  const totalCTAs = splitComponentItems.reduce((acc, e) => acc + hasCTAs(e), 0);

  const firstColumn = getFirstColumnElements({
    splitComponentItems,
    totalTitles,
    totalCTAs,
  });

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['xs', 'sm'].includes(breakpoint);
  // console.log(firstColumn, splitComponentItems);
  if (!splitComponentItems) return null;

  return (
    <section className='flex flex-col items-center justify-start w-screen pt-10'>
      <Wrapper favoritesBanner={false}>
        <StyledDiv
          className={cx(
            'flex flex-col md:flex-row items-start justify-between',
            'px-4 md:px-10-mattamy py-30-mattamy',
            'text-mattamy-blue bg-white',
          )}
        >
          {isMobile && splitComponentItems.length > 0 && (
            <div className='flex flex-col w-full md:w-1/2'>
              {splitComponentItems.map((item, index) => (
                <SplitItem data={item} key={index} />
              ))}
            </div>
          )}
          {!isMobile && splitComponentItems.length > 0 && (
            <div className='flex flex-col w-full md:w-1/2 mr-8'>
              {firstColumn.map((item, index) => (
                <SplitItem data={item} key={index} />
              ))}
            </div>
          )}
          {!isMobile && splitComponentItems.length > 0 && (
            <div className='flex flex-col w-full md:w-1/2 ml-8'>
              {splitComponentItems.map(
                (item, index) =>
                  index > firstColumn.length - 1 && (
                    <SplitItem data={item} key={index} />
                  ),
              )}
            </div>
          )}
        </StyledDiv>
      </Wrapper>
    </section>
  );
};

export default SplitComponent;
